import { ActivatedRoute, ActivatedRouteSnapshot, Route } from '@angular/router';
import { PageBreadcrumbItem } from '../components/page-breadcrumb/page-breadcrumb.types';

/** Optional i18n keys for known path segments (falls back to a humanized path). */
const SEGMENT_LABEL_KEYS: Record<string, string> = {
    'account-center': 'menu.nav.accountCenterNew',
    integrations: 'menu.nav.integrations',
    'fontes-trafego': 'menu.nav.fontesTrafego',
    'dados-assinatura': 'menu.userMenu.subscriptionData',
    integracao: 'shared.breadcrumb.salesNotifications',
    legado: 'shared.breadcrumb.previousVersion',
    adicionar: 'shared.breadcrumb.add',
    editar: 'shared.breadcrumb.edit',
    add: 'shared.breadcrumb.add',
    edit: 'shared.breadcrumb.edit',
    fees: 'menu.nav.fees',
    taxes: 'menu.nav.taxes',
    products: 'menu.nav.products',
    'url-produtor': 'menu.nav.producerUrl',
    apikey: 'menu.nav.apiKeys',
    checkout: 'menu.nav.checkout',
    'link-checkout': 'menu.nav.checkoutLink',
    'lista-checkouts': 'menu.nav.checkoutList',
    checkouts: 'menu.nav.checkoutList',
    'link-visita': 'shared.breadcrumb.visitLink',
    'link-notificacao': 'menu.nav.notificationLink',
    notificacoes: 'menu.nav.notifications',
    vendas: 'shared.breadcrumb.sales',
    dominios: 'menu.nav.ownDomains',
    projetos: 'menu.nav.projects',
    visitas: 'shared.breadcrumb.visits',
    'tag-ratoeira': 'menu.nav.tagRatoeira',
    list: 'shared.breadcrumb.list',
    ratoeiras: 'menu.nav.mousetraps',
    'url-venda': 'shared.breadcrumb.salesUrl',
    relatorios: 'menu.nav.reports',
    'funil-topo': 'shared.breadcrumb.funnelTop',
    'rato-por-ratoeira': 'shared.breadcrumb.ratPerMousetrap',
    'checkout-por-ratoeira': 'shared.breadcrumb.checkoutPerMousetrap',
    'biblioteca-de-ratos': 'shared.breadcrumb.ratLibrary',
    'google-ads-profile-list': 'shared.breadcrumb.googleAdsProfiles',
    'google-ads-profile': 'shared.breadcrumb.googleAdsProfiles',
    'meta-ads-profile-list': 'shared.breadcrumb.metaAdsProfiles',
    'meta-ads-profile': 'shared.breadcrumb.metaAdsProfiles',
    'newsbreak-ads-profile-list': 'shared.breadcrumb.newsbreakAdsProfiles',
    'newsbreak-ads-profile': 'shared.breadcrumb.newsbreakAdsProfiles',
    'taboola-ads-profile-list': 'shared.breadcrumb.taboolaAdsProfiles',
    'taboola-ads-profile': 'shared.breadcrumb.taboolaAdsProfiles',
    accounts: 'shared.breadcrumb.accounts',
    organizations: 'shared.breadcrumb.organizations',
};

export function hasNavigableBreadcrumbParent(items: PageBreadcrumbItem[] | null | undefined): boolean {
    if (!items || items.length < 2) {
        return false;
    }

    return items.slice(0, -1).some((item) => Boolean(item.link));
}

/**
 * When a route owns `data.hasBreadcrumbs: true`, any deeper URL under that section
 * gets: [section root] > [each path segment] … with parents linking only to real routes.
 */
export function buildBreadcrumbsFromRoute(route: ActivatedRoute): PageBreadcrumbItem[] {
    let snapshot: ActivatedRouteSnapshot | null = route.snapshot;
    let url = '';
    let rootUrl: string | null = null;
    let rootLabel: PageBreadcrumbItem | null = null;
    let sectionChildPaths: string[] = [];

    while (snapshot) {
        const segment = snapshot.url.map((part) => part.path).join('/');
        if (segment) {
            url += `/${segment}`;
        }

        // Use the outermost section root (first match) so nested flags cannot reset the trail.
        if (!rootUrl && snapshot.routeConfig?.data?.['hasBreadcrumbs'] === true) {
            rootUrl = url;
            rootLabel = labelForSegment(
                snapshot.url
                    .map((part) => part.path)
                    .filter((part) => !isParamLike(part))
                    .at(-1),
            );
            sectionChildPaths = collectStaticChildPaths(snapshot.routeConfig.children);
        }

        snapshot = snapshot.firstChild;
    }

    if (!rootUrl || !rootLabel || url === rootUrl) {
        return [];
    }

    const relativeSegments = url.slice(rootUrl.length).split('/').filter(Boolean);
    const childCrumbs = buildChildCrumbs(rootUrl, relativeSegments, sectionChildPaths);
    if (childCrumbs.length === 0) {
        return [];
    }

    const items: PageBreadcrumbItem[] = [{ ...rootLabel, link: rootUrl }, ...childCrumbs];
    return hasNavigableBreadcrumbParent(items) ? items : [];
}

/**
 * One crumb per non-param segment.
 * Intermediate crumbs only get a `link` when it matches a real sibling route
 * (exact path or `${segment}-list`).
 */
function buildChildCrumbs(rootUrl: string, segments: string[], sectionChildPaths: string[]): PageBreadcrumbItem[] {
    const crumbs: PageBreadcrumbItem[] = [];

    for (const segment of segments) {
        if (isParamLike(segment)) {
            continue;
        }

        const link = resolveSectionChildLink(rootUrl, segment, sectionChildPaths);
        crumbs.push({
            ...labelForSegment(segment),
            ...(link ? { link } : {}),
        });
    }

    if (crumbs.length === 0) {
        return [];
    }

    // Current page is never a link.
    const last = crumbs[crumbs.length - 1];
    crumbs[crumbs.length - 1] = { labelKey: last.labelKey, label: last.label };
    return crumbs;
}

function resolveSectionChildLink(rootUrl: string, segment: string, sectionChildPaths: string[]): string | undefined {
    // Prefer the list sibling (`google-ads-profile` → `google-ads-profile-list`).
    const candidates = [`${segment}-list`, segment];

    for (const candidate of candidates) {
        if (sectionChildPaths.includes(candidate)) {
            return `${rootUrl}/${candidate}`;
        }
    }

    // Nested static paths like `vendas/integracao`.
    const nestedPath = sectionChildPaths.find((path) => path === segment || path.endsWith(`/${segment}`));
    if (nestedPath) {
        return `${rootUrl}/${nestedPath}`;
    }

    return undefined;
}

/** Only fully static child paths (no `:params`) — those are real navigable screens. */
function collectStaticChildPaths(children: Route[] | undefined): string[] {
    if (!children?.length) {
        return [];
    }

    return [
        ...new Set(children.map((child) => child.path ?? '').filter((path) => path.length > 0 && !path.includes(':'))),
    ];
}

function labelForSegment(segment: string | undefined): PageBreadcrumbItem {
    if (!segment) {
        return { label: '' };
    }

    const labelKey = SEGMENT_LABEL_KEYS[segment];
    if (labelKey) {
        return { labelKey };
    }

    return { label: humanizeSegment(segment) };
}

function humanizeSegment(segment: string): string {
    return segment
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function isParamLike(segment: string): boolean {
    if (/^\d+$/.test(segment)) {
        return true;
    }

    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
        return true;
    }

    return segment.length > 24 && !segment.includes('-');
}
